import { OrderState } from '../../typings/Order'
import { PaymentAPI, PaymentError, PaymentResponse, PaymentState, PollPaymentStateOptions } from '../../typings/Payment'
import { PollPaymentOrderDocument, PollPaymentOrderQuery, PollPaymentOrderQueryVariables } from '../../graphql'

const POLLING_INTERVAL = 1000
const MAX_POLLING_RETRIES = 60

const pollPayment = async (
  paymentAPI: PaymentAPI,
  { orderId, lastPaymentAttempt, additionalExpectedPaymentStates = [] }: PollPaymentStateOptions,
): Promise<PaymentResponse> => {
  const getOrderStatus = paymentAPI.apolloClient.watchQuery<PollPaymentOrderQuery, PollPaymentOrderQueryVariables>({
    query: PollPaymentOrderDocument,
    variables: { orderId },
  })

  return new Promise((resolve, reject) => {
    let numberOfPollingTries = 0

    const poll = async (): Promise<void> => {
      const { data } = await getOrderStatus.refetch()

      if (!data || (data && !data.order)) return

      const { order } = data

      if (numberOfPollingTries++ > MAX_POLLING_RETRIES) {
        return resolve({ wasSuccessful: false, error: PaymentError.PAYMENT_ERROR_REQUEST_TIMEOUT })
      }
      if (!order) {
        throw new Error('order was expected')
      }
      const { state: orderState, payment } = order
      if (!payment) {
        throw new Error('payment was expected')
      }
      const { state: paymentState } = payment

      // payment and order transitioned both to a successful state
      // when we enter the checkout there are two states possible:
      // - invoice checkout: order: confirmed, payment: open
      // - purchase checkout: order: open, payment: open
      if (
        orderState === OrderState.ORDER_STATE_CONFIRMED &&
        [PaymentState.PAYMENT_STATE_SUCCEEDED, ...additionalExpectedPaymentStates].includes(paymentState)
      ) {
        return resolve({ wasSuccessful: true })
      }

      // payment was aborted by server => leave checkout
      if (paymentState === PaymentState.PAYMENT_STATE_CANCELLED) {
        return resolve({ wasSuccessful: false, error: PaymentError.PAYMENT_ERROR_CANCELLED })
      }

      // leave the checkout
      if (orderState === OrderState.ORDER_STATE_ABORTED) {
        return resolve({ wasSuccessful: false, error: PaymentError.PAYMENT_ERROR_ORDER_ABORTED })
      }

      // when entering the polling after a failed payment attempt the payment state is still FAILED and transitions to FAILED/SUCCEEDED
      // to check that we get an updated state => compare the timestamps of the initial payment and the fetched payment
      if (
        payment.lastAttemptAt &&
        lastPaymentAttempt &&
        lastPaymentAttempt.getTime() >= new Date(payment.lastAttemptAt).getTime()
      ) {
        setTimeout(poll, POLLING_INTERVAL)
        return
      }

      // payment was rejected by payment provider => stay on payment screen and use another payment method
      if (paymentState === PaymentState.PAYMENT_STATE_FAILED) {
        return resolve({ wasSuccessful: false, error: PaymentError.PAYMENT_ERROR_FAILED })
      }

      setTimeout(poll, POLLING_INTERVAL)
    }

    poll().catch(reject)
  })
}

export default pollPayment
